import { EditOutlined } from '@ant-design/icons';
import { useOne } from '@refinedev/core';
import { Button, Form, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import EditableModal, { useEditableModal } from '@/components/general/EditableModal';
import LinkTextField from '@/components/general/LinkTextField';
import { EditableInfoItem, ShowInfoGrid } from '@/components/general/ShowInfo/ShowInfo';
import { COMPANY_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ReceptionShowTechnicianProps {
  record?: Reception;
  refetch?: () => void;
}

const ReceptionShowTechnician = (props: ReceptionShowTechnicianProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const { record, refetch } = props;
  const { receptionRepository } = useRepositoriesContext();
  const { activeModal, openModal, closeModal } = useEditableModal<'technician'>();
  const {
    result: technicianResult,
    query: { isLoading: technicianIsLoading },
  } = useOne({
    resource: 'empresas',
    id: record?.technician || '',
    queryOptions: {
      enabled: !!record && !!record.technician,
      retry: false,
    },
  });
  const { selectProps: technicianSelectProps } = useSelectScrollSearch({
    resource: 'empresas',
    optionLabel: (item: any) => item.full_name,
    meta: {
      type: 5,
      id: 'technician',
    },
    queryOptions: {
      queryKey: ['empresas', 'technicians'],
    },
  });
  const technicianSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: technicianSelectProps,
    resource: 'empresas',
    initialValue: record?.technician,
    labelKey: 'full_name',
    valueKey: 'id',
  });

  return (
    <ShowInfoGrid>
      <EditableInfoItem
        label={t('Técnico involucrado')}
        action={
          hasPermission('edit') &&
          record?.status == PACKAGE_STATES.PENDIENTE && (
            <Button icon={<EditOutlined />} size="small" onClick={() => openModal('technician')} />
          )
        }>
        {technicianIsLoading ? (
          <Skeleton />
        ) : record?.technician ? (
          <LinkTextField
            label={`${technicianResult?.full_name} (${technicianResult?.id})`}
            to={`${COMPANY_URLS.ADMIN_EMPRESA}/${technicianResult?.id}`}
            admin
          />
        ) : (
          '-'
        )}
      </EditableInfoItem>

      <EditableModal
        activeModal={activeModal}
        closeModal={closeModal}
        modalConfigByKey={{
          technician: {
            title: t('Cambiar técnico involucrado'),
            form: {
              initialValues: { technicianId: record?.technician },
              // Metadata: actualiza el técnico desde el modal compartido y refresca la vista al guardar.
              onFinish: async values => {
                if (!record?.id) return false;
                if (values.technicianId === record.technician) return;

                await receptionRepository.updateReceptionTechnician(record.id, values.technicianId);
                refetch?.();
              },
              children: (
                <Form.Item label={t('Técnico involucrado')} required={true} name="technicianId">
                  <AntdSelect {...technicianSelectPropsWithInitials} allowClear showSearch />
                </Form.Item>
              ),
            },
          },
        }}
      />
    </ShowInfoGrid>
  );
};

export default ReceptionShowTechnician;
